import { z } from 'zod';
/**
 * Health & readiness contracts (KIR-3589).
 *
 * Three endpoints share one snapshot shape (`healthSnapshotSchema`):
 *
 * - `GET /livez` — **liveness**. Always HTTP 200 while the process is serving.
 *   The body reports startup + dependency status for observability, but a dead
 *   dependency never makes it 503 — gating liveness on dependencies would cause
 *   restart storms. Point the Cloud Run *liveness* probe here.
 * - `GET /readyz` — **readiness / traffic gate**. HTTP 200 iff every startup
 *   phase is `ready` AND the database is reachable (debounced); otherwise 503.
 *   Point the Cloud Run *readiness/startup* probe + load balancer here. Only
 *   the database gates traffic — `kirin-ai`/`osv-api` are report-only and
 *   fail-open in the scan path, and so is `redis` (the roster cache falls
 *   back to Descope).
 * - `GET /healthz` — informational. Always HTTP 200 while serving; the body
 *   carries the same snapshot so dashboards / uptime checks read real state
 *   without paging on transient blips.
 *
 * `GET /health` (legacy, {@link healthResponseSchema}) is retained for
 * backwards compatibility only — prefer the endpoints above.
 */
/**
 * Per-dependency check outcome.
 *
 * - `healthy` — the dependency responded successfully within its timeout.
 * - `unhealthy` — the dependency responded with a failure (or the check threw).
 * - `timeout` — the dependency did not respond within its per-check budget.
 */
export const dependencyStatusSchema = z.enum([
    'healthy',
    'unhealthy',
    'timeout',
]);
export const dependencyCheckSchema = z.object({
    status: dependencyStatusSchema,
    // Wall-clock milliseconds the check took (including a timed-out wait).
    latencyMs: z.number().nonnegative(),
    // Truncated failure detail, or `null` when the check succeeded.
    error: z.string().nullable(),
});
/**
 * Redis check (KIR-4887). Same shape as {@link dependencyCheckSchema} plus a
 * `disabled` status for an instance with no Redis configured (`REDIS_HOST`
 * unset). Report-only: every Redis caller fails open to Descope, so Redis
 * never affects `status` or `/readyz`.
 */
export const redisDependencyCheckSchema = dependencyCheckSchema.extend({
    status: z.enum([...dependencyStatusSchema.options, 'disabled']),
});
/**
 * State of a single startup phase.
 *
 * - `pending` — not yet completed (server is still booting).
 * - `ready` — completed successfully.
 * - `failed` — completed with an error (for a gating phase, the process exits).
 * - `degraded` — a **non-gating** phase completed by falling back to safe
 *   defaults because a shared external dependency was unreachable (today:
 *   `feature_flags` when Flagsmith is down). The instance still serves — the
 *   fallback is a deliberate posture — but operators should be alerted.
 */
export const startupPhaseStateSchema = z.enum([
    'pending',
    'ready',
    'failed',
    'degraded',
]);
/**
 * The startup phases tracked for readiness. `/readyz` requires every *gating*
 * phase to be `ready`. `feature_flags` is intentionally **non-gating**: it
 * fails open to default flag values (so a Flagsmith outage — a shared upstream
 * that's down for the whole fleet at once — can't cause a correlated,
 * fleet-wide readiness outage). Its `degraded` state is surfaced here for
 * observability only.
 */
export const startupPhaseNameSchema = z.enum([
    'feature_flags',
    'untrusted_extensions',
    'db',
    'job_queue',
]);
export const startupPhasesSchema = z.object({
    feature_flags: startupPhaseStateSchema,
    untrusted_extensions: startupPhaseStateSchema,
    db: startupPhaseStateSchema,
    job_queue: startupPhaseStateSchema,
});
export const startupSnapshotSchema = z.object({
    // True iff every phase is `ready`.
    ready: z.boolean(),
    phases: startupPhasesSchema,
});
export const healthStatusSchema = z.enum(['healthy', 'unhealthy']);
/**
 * Shared body for `GET /livez` and `GET /healthz`.
 *
 * `version`, `commitHash`, `buildTime` are baked into the backend image at
 * docker-build time. They can be `null` only in local/dev runs where the
 * corresponding env vars and `.commit-sha` file are absent.
 *
 * `nodeVersion` is a runtime value read from the live process — always
 * present, hence non-nullable.
 */
export const healthSnapshotSchema = z.object({
    // Composite health: `healthy` iff startup is ready AND every dependency is
    // healthy. Drives the `/livez` HTTP status; on `/healthz` it is body-only.
    status: healthStatusSchema,
    startup: startupSnapshotSchema,
    dependencies: z.object({
        db: dependencyCheckSchema,
        kirin_ai: dependencyCheckSchema,
        osv_api: dependencyCheckSchema,
        // Optional so a client parses a backend that predates the Redis check.
        redis: redisDependencyCheckSchema.optional(),
    }),
    version: z.string().nullable(),
    // ISO 8601 UTC stamp baked at docker-build time. Nullable for local builds
    // where the env var is absent.
    buildTime: z.string().datetime().nullable(),
    commitHash: z.string().nullable(),
    // Node.js version of the *running* backend process (`process.version`,
    // e.g. "v24.16.0"). Surfaces the actual runtime Node so a base-image
    // version drift (see KIR-3423) is observable. Non-nullable.
    nodeVersion: z.string(),
    timestamp: z.string().datetime(),
});
/** Response shape for `GET /livez` (liveness probe; always 200 while serving). */
export const livezResponseSchema = healthSnapshotSchema;
/** Response shape for `GET /readyz` (traffic-gating readiness probe). */
export const readyzResponseSchema = healthSnapshotSchema;
/** Response shape for `GET /healthz` (informational, always 200). */
export const healthzResponseSchema = healthSnapshotSchema;
/**
 * Legacy `GET /health` response (KIR-3589). **Deprecated** — retained only for
 * backwards compatibility with pre-existing external probes/consumers. New
 * code should use `/livez`, `/readyz`, or `/healthz` and {@link HealthSnapshot}.
 */
export const healthDatabaseStatusSchema = z.enum(['connected', 'disconnected']);
export const healthResponseSchema = z.object({
    status: z.literal('healthy'),
    database: healthDatabaseStatusSchema,
    timestamp: z.string().datetime(),
    version: z.string().nullable(),
    buildTime: z.string().datetime().nullable(),
    commitHash: z.string().nullable(),
    nodeVersion: z.string(),
});
//# sourceMappingURL=health.js.map